import { useEffect, useState, useRef } from "react";
import { Modal, message, Spin, Button } from "antd";
import QRCode from "react-qr-code";
import axios from "axios";
import messages from "../../errors/messages.en-IN.json";
import useCCAvenuePendingStore from "../../stores/useCCAvenuePendingStore";
import { trace } from "../../utils/debugTrace";

// CCAvenue's QR opens a hosted card-entry + 3D-Secure page, not a scan-and-approve
// UPI flow — real completions in this environment have taken anywhere from ~1 to
// ~40 minutes. QR_DISPLAY_SECONDS is a reasonable in-store foreground wait; once it
// (or an explicit Cancel) elapses without a final status, the payment is handed off
// to useCCAvenuePendingStore for background reconciliation instead of being marked
// failed outright — see the poller in EventBusProvider.js.
const QR_DISPLAY_SECONDS = 150;
const VERIFY_EXTRA_SECONDS = 30;
const COUNTDOWN_START_SECONDS = 30;
const POLL_INTERVAL_MS = 1000;

const CCAvenueQRModal = ({ ccAvenueData, paymentMethod, visible, onClose, ccAvenuePaymentCallback, host, providerConfigurations }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const cleanupRef = useRef(false);

  const backgroundAndResolve = (finalStatus) => {
    if (ccAvenueData?.orderId) {
      useCCAvenuePendingStore.getState().addPending({
        orderId: ccAvenueData.orderId,
        amount: ccAvenueData.amount,
        paymentMethodName: paymentMethod?.name,
        createdAt: Date.now(),
      });
    }
    message.info("Still waiting on this CCAvenue payment — you'll be notified once it's confirmed.");
    ccAvenuePaymentCallback(finalStatus || { order_status: "Backgrounded" });
    onClose();
  };

  const handleCancel = async () => {
    if (isCancelling) return;
    setIsCancelling(true);
    try {
      const stringifiedConfig = providerConfigurations ? JSON.stringify(providerConfigurations) : null;
      const statusResponse = await axios.get(`${host}/poll-status`, {
        params: { order_id: ccAvenueData.orderId, payloadData: stringifiedConfig },
      });
      if (statusResponse.data?.order_status === "Success") {
        ccAvenuePaymentCallback(statusResponse.data);
        onClose();
        return;
      }
    } catch (error) {
      console.error("Error checking CC Avenue status before cancel:", error);
    }
    backgroundAndResolve();
    setIsCancelling(false);
  };

  useEffect(() => {
    if (!visible || !ccAvenueData?.transactionUrl) return;
    cleanupRef.current = false;

    // BUG-1786 diagnostics: capture the exact URL encoded into the QR so a
    // production repro can confirm whether the customer's phone is being
    // sent to the CCAvenue hosted page at all, or something else.
    trace("CCAvenue", "QR modal opened", {
      orderId: ccAvenueData.orderId,
      transactionUrl: ccAvenueData.transactionUrl,
    });

    let poll;
    let timer;

    const pollPaymentStatus = () => {
      let attempts = 0;
      const initialMaxAttempts = QR_DISPLAY_SECONDS;
      const extraAttempts = VERIFY_EXTRA_SECONDS;
      const maxAttempts = initialMaxAttempts + extraAttempts;
      const interval = POLL_INTERVAL_MS;
      const countdownStart = COUNTDOWN_START_SECONDS;

      const stringifiedConfig = providerConfigurations ? JSON.stringify(providerConfigurations) : null;

      poll = setInterval(async () => {
        if (cleanupRef.current) {
          clearInterval(poll);
          return;
        }

        attempts += 1;
        const secondsElapsed = attempts * (interval / 1000);
        const initialDuration = initialMaxAttempts * (interval / 1000);
        const remainingInitialSeconds = initialDuration - secondsElapsed;

        if (remainingInitialSeconds <= countdownStart && attempts <= initialMaxAttempts && !timer) {
          setTimeLeft(Math.ceil(remainingInitialSeconds));
          timer = setInterval(() => {
            setTimeLeft((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                setTimeLeft(null);
                setIsLoading(true);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }

        try {
          const statusResponse = await axios.get(`${host}/poll-status`, {
            params: { order_id: ccAvenueData.orderId, payloadData: stringifiedConfig },
          });
          const { order_status } = statusResponse.data;

          if (order_status === "Success") {
            trace("CCAvenue", "poll-status: Success", { orderId: ccAvenueData.orderId, attempts, data: statusResponse.data });
            clearInterval(poll);
            if (timer) clearInterval(timer);
            setIsLoading(false);
            setTimeLeft(null);
            ccAvenuePaymentCallback(statusResponse.data);
            onClose();
          } else if (order_status === "Failure" || order_status === "Aborted") {
            trace("CCAvenue", "poll-status: terminal failure", { orderId: ccAvenueData.orderId, attempts, order_status, data: statusResponse.data });
            clearInterval(poll);
            if (timer) clearInterval(timer);
            setIsLoading(false);
            setTimeLeft(null);
            message.error(`${messages.info.payment_failed}`);
            ccAvenuePaymentCallback({ order_status });
            onClose();
          } else if (attempts >= maxAttempts) {
            // Still pending after the foreground wait — hand off to background
            // reconciliation instead of declaring it failed; it may well still succeed.
            trace("CCAvenue", "poll-status: backgrounded (still pending)", { orderId: ccAvenueData.orderId, attempts, lastStatus: order_status });
            clearInterval(poll);
            if (timer) clearInterval(timer);
            setIsLoading(false);
            setTimeLeft(null);
            backgroundAndResolve();
          }
        } catch (error) {
          if (attempts >= maxAttempts) {
            trace("CCAvenue", "poll-status: error at max attempts", { orderId: ccAvenueData.orderId, attempts, error: error.message });
            clearInterval(poll);
            if (timer) clearInterval(timer);
            setIsLoading(false);
            setTimeLeft(null);
            message.error(`${messages.info.payment_failed}`);
            console.error("Error polling CC Avenue status:", error);
            ccAvenuePaymentCallback({ order_status: "Error" });
            onClose();
          }
        }
      }, interval);
    };

    pollPaymentStatus();

    return () => {
      cleanupRef.current = true;
      if (poll) clearInterval(poll);
      if (timer) clearInterval(timer);
      setTimeLeft(null);
      setIsLoading(false);
    };
  }, [visible, ccAvenueData, ccAvenuePaymentCallback, onClose, host, providerConfigurations]);

  return (
    <Modal
      title={isLoading ? "Processing Payment" : "Scan to Pay"}
      open={visible}
      onCancel={handleCancel}
      maskClosable={false}
      keyboard={false}
      closable={false}
      centered
      footer={[
        <Button key="cancel" onClick={handleCancel} loading={isCancelling}>
          Cancel Payment
        </Button>,
      ]}
      styles={{
        body: {
          padding: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        },
      }}
    >
      {ccAvenueData?.transactionUrl && !isLoading ? (
        <div style={{ textAlign: "center", width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
            <QRCode value={ccAvenueData.transactionUrl} size={300} level="M" />
          </div>
          <p style={{ marginTop: "20px", fontSize: "16px" }}>Scan the QR code to complete the payment</p>
          {timeLeft !== null && (
            <p style={{ marginTop: "10px", fontSize: "16px", color: "red" }}>Time remaining: {timeLeft} seconds</p>
          )}
        </div>
      ) : isLoading ? (
        <div style={{ textAlign: "center", width: "100%" }}>
          <Spin size="large" />
          <p style={{ marginTop: "16px", fontSize: "14px", color: "#666" }}>Verifying payment status...</p>
        </div>
      ) : null}
    </Modal>
  );
};

export default CCAvenueQRModal;
